import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname.startsWith('/admin')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (email, password, name) => api.post('/auth/register', { email, password, name }),
  registerWithInvite: (email, password, name, invite_token) => 
    api.post('/auth/register-invite', { email, password, name, invite_token }),
  // 12s timeout so a hanging /auth/me can never freeze the app on cold boot
  getMe: () => api.get('/auth/me', { timeout: 12000 }),
  impersonateAgent: (agentId) => api.post(`/auth/impersonate/${agentId}`),
};

export const invitesAPI = {
  create: (email) => api.post('/auth/invites', { email }),
  getAll: () => api.get('/auth/invites'),
  revoke: (inviteId) => api.delete(`/auth/invites/${inviteId}`),
};

// Team Management API
export const teamAPI = {
  getMembers: (params) => api.get('/team/members', { params }),
  getMember: (id) => api.get(`/team/members/${id}`),
  createMember: (data) => api.post('/team/members', data),
  updateMember: (id, data) => {
    // Use PATCH with JSON for complex data (custom_commission_rules)
    // Falls back to PUT with FormData for simple updates
    if (data.custom_commission_rules !== undefined || data.use_custom_rules !== undefined) {
      return api.patch(`/team/members/${id}`, data);
    }
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    return api.put(`/team/members/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteMember: (id) => api.delete(`/team/members/${id}`),
  resendInvite: (id) => api.post(`/team/members/${id}/resend-invite`),
  getLeaderboard: (period = 'month') => api.get('/team/leaderboard', { params: { period } }),
  getSalesAgents: () => api.get('/team/sales-agents'),
};

// Sales API
export const salesAPI = {
  getSales: (params) => api.get('/team/sales', { params }),
  createSale: (data) => api.post('/team/sales', data),
  getSummary: (period) => api.get('/team/sales/summary', { params: { period } }),
};

// Referral Links API
export const referralLinksAPI = {
  getAll: () => api.get('/team/referral-links'),
  create: (data) => api.post('/team/referral-links', data),
  send: (linkId, data) => {
    const formData = new FormData();
    formData.append('contact_type', data.contact_type);
    formData.append('contact_value', data.contact_value);
    if (data.message) formData.append('message', data.message);
    return api.post(`/team/referral-links/${linkId}/send`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  track: (code) => api.get(`/team/referral-links/${code}/track`),
};

// Commission Settings API
export const commissionAPI = {
  getSettings: () => api.get('/commissions/settings'),
  getSettingsForSales: () => api.get('/commissions/settings/sales-view'),
  getSetting: (serviceType) => api.get(`/commissions/settings/${serviceType}`),
  createSetting: (data) => api.post('/commissions/settings', data),
  updateSetting: (id, data) => api.put(`/commissions/settings/${id}`, data),
  deleteSetting: (id) => api.delete(`/commissions/settings/${id}`),
  calculate: (serviceType, amount) => 
    api.post(`/commissions/calculate?service_type=${serviceType}&sale_amount=${amount}`),
  // Reports
  getReportSummary: (period) => api.get('/commissions/reports/summary', { params: { period } }),
  getReportByAgent: (period, agentId) => api.get('/commissions/reports/by-agent', { 
    params: { period, agent_id: agentId } 
  }),
  getAgentReport: (agentId, period) => api.get(`/commissions/reports/agent/${agentId}`, { 
    params: { period } 
  }),
  // Production commission policy + funded-deal flow
  getPolicy: () => api.get('/commissions/policy'),
  getTiers: () => api.get('/commissions/tiers'),
  setRepTier: (agentId, tier) => api.put(`/commissions/agent/${agentId}/tier`, { tier }),
  recordFundedDeal: (data) => api.post('/commissions/record-funded-deal', data),
  setPayoutStatus: (entryId, status) => api.post(`/commissions/payout/${entryId}/status`, { status }),
  getPayoutTasks: (status) => api.get('/commissions/payout-tasks', { params: { status } }),
};

// Accounting API
export const accountingAPI = {
  getExpenses: (params) => api.get('/accounting/expenses', { params }),
  createExpense: (data) => api.post('/accounting/expenses', data),
  updateExpense: (id, data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    return api.put(`/accounting/expenses/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteExpense: (id) => api.delete(`/accounting/expenses/${id}`),
  importCSV: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/accounting/expenses/import-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getProfitLoss: (params) => api.get('/accounting/profit-loss', { params }),
  getTaxDocuments: (params) => api.get('/accounting/tax-documents', { params }),
  sendTaxDocument: (documentType, agentId) => {
    const formData = new FormData();
    formData.append('document_type', documentType);
    formData.append('agent_id', agentId);
    return api.post('/accounting/tax-documents/send', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  sendTaxDocumentExternal: (email, name) => {
    const formData = new FormData();
    formData.append('email', email);
    formData.append('name', name);
    return api.post('/accounting/tax-documents/send-external', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  fillTaxDocument: (docId, data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => formData.append(key, data[key]));
    return api.post(`/accounting/tax-documents/${docId}/fill`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  signTaxDocument: (docId, signatureData) => {
    const formData = new FormData();
    formData.append('signature_data', signatureData);
    return api.post(`/accounting/tax-documents/${docId}/sign`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadTaxDocument: (docId) => api.get(`/accounting/tax-documents/${docId}/download`, { responseType: 'blob' }),
  getAgentProfiles: () => api.get('/accounting/agent-profiles'),
  getAgentProfile: (id) => api.get(`/accounting/agent-profiles/${id}`),
};

// E-Signature API (DocuSeal)
export const esignAPI = {
  // Status & Templates
  getStatus: () => api.get('/esign/status'),
  getTemplates: () => api.get('/esign/templates'),
  getTemplate: (id) => api.get(`/esign/templates/${id}`),
  uploadW9Template: () => api.post('/esign/templates/upload-w9'),
  
  // Submissions
  createSubmission: (data) => api.post('/esign/submissions', data),
  getSubmissions: (params) => api.get('/esign/submissions', { params }),
  getSubmission: (id) => api.get(`/esign/submissions/${id}`),
  downloadSignedDocument: (id) => api.get(`/esign/submissions/${id}/download`, { responseType: 'blob' }),
  
  // Tax Documents W-9
  sendW9: (data) => api.post('/esign/send-w9', data),
  
  // Agent Compliance (W-9/1099)
  getAgentComplianceStatus: (agentId) => api.get(`/esign/agent/${agentId}/compliance`),
  requestW9ForAgent: (agentId) => api.post(`/esign/agent/${agentId}/request-w9`),
  getAgentsComplianceSummary: () => api.get('/esign/agents/compliance-summary'),
  
  // 1099 Documents
  upload1099: (data) => api.post('/esign/1099/upload', data),
  download1099: (docId) => api.get(`/esign/1099/${docId}/download`, { responseType: 'blob' }),
};

// W-9 Automation API
export const w9API = {
  // Agent W-9 Status
  getMyStatus: () => api.get('/w9/my-status'),
  getAgentStatus: (agentId) => api.get(`/w9/status/${agentId}`),
  resendW9: (agentId) => api.post(`/w9/resend/${agentId}`),
  markComplete: (agentId, submissionId) => api.post(`/w9/mark-complete/${agentId}`, { submission_id: submissionId }),
  
  // Payout Eligibility
  checkPayoutEligibility: (agentId) => api.get(`/w9/payout-eligible/${agentId}`),
  
  // Audit Logs
  getAgentAuditLog: (agentId) => api.get(`/w9/audit-log/${agentId}`),
  getAllAuditLogs: (params) => api.get('/w9/audit-log', { params }),
  
  // Cron/Admin
  processReminders: () => api.post('/w9/process-reminders'),
  getCronStatus: () => api.get('/w9/cron-status'),
};

// Banking API (Teller.io)
export const bankingAPI = {
  getStatus: () => api.get('/banking/status'),
  getConnectConfig: () => api.get('/banking/connect-config'),
  saveConnection: (data) => api.post('/banking/connect', data),
  getConnections: () => api.get('/banking/connections'),
  disconnectBank: (enrollmentId) => api.delete(`/banking/connections/${enrollmentId}`),
  getAccounts: (enrollmentId) => api.get(`/banking/accounts/${enrollmentId}`),
  getTransactions: (enrollmentId, accountId, params) => 
    api.get(`/banking/accounts/${enrollmentId}/${accountId}/transactions`, { params }),
  getBalance: (enrollmentId, accountId) => 
    api.get(`/banking/accounts/${enrollmentId}/${accountId}/balance`),
  importExpenses: (data) => api.post('/banking/import-expenses', data),
  syncAll: () => api.post('/banking/sync-all'),
};

// Payout Tracking API
export const payoutAPI = {
  // Commission Payouts
  getCommissionPayouts: (params) => api.get('/payouts/commissions', { params }),
  createCommissionPayout: (data) => api.post('/payouts/commissions', data),
  getCommissionSummary: (year) => api.get('/payouts/commissions/summary', { params: { year } }),
  
  // Referral Payouts
  getReferralPayouts: (params) => api.get('/payouts/referrals', { params }),
  createReferralPayout: (data) => api.post('/payouts/referrals', data),
  getReferralSummary: (year) => api.get('/payouts/referrals/summary', { params: { year } }),
  updateW9Status: (payoutId, status, documentId) => 
    api.patch(`/payouts/referrals/${payoutId}/w9-status`, null, { 
      params: { status, document_id: documentId } 
    }),
};

// Agent Commission Rules API
export const agentCommissionAPI = {
  getAgentRules: (agentId) => api.get(`/commissions/agent/${agentId}/rules`),
  updateAgentRules: (agentId, data) => api.put(`/commissions/agent/${agentId}/rules`, data),
  calculateWithAgent: (serviceType, saleAmount, agentId) => 
    api.post('/commissions/calculate', null, { 
      params: { service_type: serviceType, sale_amount: saleAmount, agent_id: agentId } 
    }),
};

// Invitation API
export const invitationAPI = {
  send: (data) => api.post('/invitations/send', data),
  verify: (token) => api.get(`/invitations/verify/${token}`),
  accept: (data) => api.post('/invitations/accept', data),
  getPending: () => api.get('/invitations/pending'),
  resend: (memberId) => api.post(`/invitations/resend/${memberId}`),
  cancel: (memberId) => api.delete(`/invitations/cancel/${memberId}`),
};

// Password Reset API
export const passwordResetAPI = {
  requestReset: (email) => api.post('/auth/forgot-password', { email }),
  verifyToken: (token) => api.get(`/auth/verify-reset-token/${token}`),
  resetPassword: (token, newPassword) => 
    api.post('/auth/reset-password', { token, new_password: newPassword }),
};

export const emailTrackingAPI = {
  getStats: () => api.get('/email-tracking/stats'),
  getHistory: () => api.get('/email-tracking/history'),
};

// Leads API
export const leadsAPI = {
  create: (data) => api.post('/leads', data),
  getAll: (params) => api.get('/leads', { params }),
  getById: (id) => api.get(`/leads/${id}`),
  update: (id, data) => api.patch(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  download: (id) => api.get(`/leads/${id}/download`),
  getAgreement: (id) => api.get(`/leads/${id}/agreement`),
  getSensitive: (id) => api.get(`/leads/${id}/sensitive`),
  // Lead Assignment
  assign: (leadId, agentId) => api.put(`/leads/${leadId}/assign`, { agent_id: agentId }),
  bulkAssign: (leadIds, agentId) => api.post('/leads/bulk-assign', { lead_ids: leadIds, agent_id: agentId }),
  // Lead Notes
  getNotes: (leadId) => api.get(`/leads/${leadId}/notes`),
  addNote: (leadId, content, noteType = 'general') => api.post(`/leads/${leadId}/notes`, { content, note_type: noteType }),
  deleteNote: (leadId, noteId) => api.delete(`/leads/${leadId}/notes/${noteId}`),
  // Payment Plans
  getPaymentPlan: (leadId) => api.get(`/leads/${leadId}/payment-plan`),
  createPaymentPlan: (leadId, data) => api.post(`/leads/${leadId}/payment-plan`, data),
  markPaymentPaid: (leadId, paymentId, data) => api.put(`/leads/${leadId}/payment-plan/payments/${paymentId}/mark-paid`, data),
  // Calendar Events (LEGACY - use calendarAPI instead)
  getCalendarEvents: (leadId) => api.get(`/leads/${leadId}/calendar-events`),
  createCalendarEvent: (leadId, data) => api.post(`/leads/${leadId}/calendar-events`, data),
  updateCalendarEvent: (leadId, eventId, data) => api.put(`/leads/${leadId}/calendar-events/${eventId}`, data),
  deleteCalendarEvent: (leadId, eventId) => api.delete(`/leads/${leadId}/calendar-events/${eventId}`),
  // Agent Dashboard
  getAgentDashboard: () => api.get('/leads/agent/dashboard'),
  // Agent Referral Analytics
  getAgentReferralAnalytics: () => api.get('/leads/analytics/by-agent'),
};

// Centralized Calendar API
export const calendarAPI = {
  // Get events with optional filters
  getEvents: (params = {}) => api.get('/calendar', { params }),
  // Get events for a specific lead
  getLeadEvents: (leadId) => api.get(`/calendar/lead/${leadId}`),
  // Get upcoming events
  getUpcoming: (days = 7, limit = 10) => api.get('/calendar/upcoming', { params: { days, limit } }),
  // Get single event
  getEvent: (eventId) => api.get(`/calendar/${eventId}`),
  // Create event
  create: (data) => api.post('/calendar', data),
  // Update event
  update: (eventId, data) => api.put(`/calendar/${eventId}`, data),
  // Delete event
  delete: (eventId) => api.delete(`/calendar/${eventId}`),
  // Mark event as complete
  complete: (eventId) => api.put(`/calendar/${eventId}`, { status: 'completed' }),
  // Get stats
  getStats: () => api.get('/calendar/stats/summary'),
  // Google Calendar
  getGoogleStatus: () => api.get('/oauth/calendar/status'),
  connectGoogle: () => api.get('/oauth/calendar/auth/start'),
  disconnectGoogle: () => api.post('/oauth/calendar/disconnect'),
  syncToGoogle: () => api.post('/oauth/calendar/sync'),
  importFromGoogle: () => api.post('/oauth/calendar/import'),
};

// Services API
export const servicesAPI = {
  getAll: () => api.get(`/services/list`),
  getBySlug: (slug) => api.get(`/services/${slug}`),
  update: (slug, data) => api.patch(`/services/${slug}`, data),
};

// Email Campaigns API
export const emailCampaignsAPI = {
  create: (data) => api.post('/campaigns/email', data),
  getAll: () => api.get('/campaigns/email'),
  send: (id) => api.post(`/campaigns/email/${id}/send`),
};

// SMS Campaigns API
export const smsCampaignsAPI = {
  create: (data) => api.post('/campaigns/sms', data),
  getAll: () => api.get('/campaigns/sms'),
  send: (id) => api.post(`/campaigns/sms/${id}/send`),
};

// Social Posts API
export const socialPostsAPI = {
  create: (data) => api.post('/social-posts', data),
  getAll: () => api.get('/social-posts'),
};

// AI API
export const aiAPI = {
  generate: (prompt, type) => api.post('/ai/generate', { prompt, type }),
};

// Analytics API
export const analyticsAPI = {
  get: () => api.get('/analytics'),
};

// Notifications API
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/mark-all-read'),
  delete: (id) => api.delete(`/notifications/${id}`),
  clearAll: () => api.delete('/notifications/clear-all'),
};

// Internal Chat API
export const internalChatAPI = {
  // Conversations
  getConversations: (includeArchived = false) => 
    api.get('/internal-chat/conversations', { params: { include_archived: includeArchived } }),
  createConversation: (data) => api.post('/internal-chat/conversations', data),
  getConversation: (id) => api.get(`/internal-chat/conversations/${id}`),
  archiveConversation: (id) => api.delete(`/internal-chat/conversations/${id}`),
  addParticipants: (id, participantIds) => 
    api.post(`/internal-chat/conversations/${id}/participants`, participantIds),
  
  // Messages
  getMessages: (conversationId, limit = 50, before = null) => 
    api.get(`/internal-chat/conversations/${conversationId}/messages`, { 
      params: { limit, before } 
    }),
  sendMessage: (conversationId, data) => 
    api.post(`/internal-chat/conversations/${conversationId}/messages`, data),
  markConversationRead: (conversationId) => 
    api.post(`/internal-chat/conversations/${conversationId}/read`),
  
  // Search & Users
  searchMessages: (query, conversationId = null) => 
    api.post('/internal-chat/search', { query, conversation_id: conversationId }),
  getAvailableUsers: () => api.get('/internal-chat/users/available'),
  getUnreadCount: () => api.get('/internal-chat/unread-count'),
};

export default api;
